import { useEffect, useState } from "react";
import { Share, ArrowDown, X, Plus } from "lucide-react";

/**
 * In-app iOS install prompt.
 *
 * When the user opens the mobile capture page in iOS Safari (and isn't already
 * running the installed PWA), show a small dismissable card explaining exactly
 * how to add Maison Pilet to their home screen.
 *
 * Detection heuristics:
 *  - iOS: /iP(hone|od|ad)/ in userAgent (matches iPhone 15 Pro through iPad)
 *  - Standalone: navigator.standalone === true (iOS Safari PWA flag)
 *
 * The banner is dismissable and remembers the choice in localStorage.
 */
export default function IOSInstallPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const ua = navigator.userAgent || "";
      const isIOS = /iP(hone|od|ad)/.test(ua);
      // Chrome/Firefox/Edge on iOS include "CriOS", "FxiOS", "EdgiOS" in UA.
      // We only want *Safari*, which doesn't include those tokens.
      const isSafari = !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(ua);
      // Already installed to home screen?
      const isStandalone =
        window.navigator.standalone === true ||
        window.matchMedia?.("(display-mode: standalone)").matches;
      // User already dismissed the prompt this device?
      const dismissed = localStorage.getItem("mp-install-dismissed") === "1";

      if (isIOS && isSafari && !isStandalone && !dismissed) {
        setVisible(true);
      }
    } catch {
      // Fail closed — never block the app if detection breaks.
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem("mp-install-dismissed", "1");
    } catch {
      // localStorage disabled — still hide for this session
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="ios-install-prompt"
      className="bg-ivory border border-brass/50 mx-4 mt-4 mb-3 p-4 shadow-sm relative"
    >
      <button
        onClick={dismiss}
        aria-label="dismiss"
        data-testid="ios-install-dismiss"
        className="absolute top-2 right-2 text-taupe -m-1 p-1"
      >
        <X className="w-4 h-4" />
      </button>
      <p className="text-[10px] uppercase tracking-[0.2em] text-brass mb-2">install to home screen</p>
      <p className="text-sm text-charcoal mb-3 leading-relaxed">
        add maison pilet to your iphone for faster sourcing.
      </p>
      <ol className="space-y-2 text-xs text-espresso">
        <li className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-brass text-ivory flex items-center justify-center text-[10px] font-medium shrink-0">1</span>
          <span className="flex items-center gap-1">
            tap
            <Share className="w-4 h-4 mx-1 text-brass inline-block" strokeWidth={1.8} />
            <strong className="text-charcoal">share</strong>
            <span className="text-taupe italic">(at the very bottom of safari — scroll up to reveal if hidden)</span>
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-brass text-ivory flex items-center justify-center text-[10px] font-medium shrink-0">2</span>
          <span className="flex items-center gap-1">
            scroll & tap
            <Plus className="w-4 h-4 mx-1 text-brass inline-block" strokeWidth={2} />
            <strong className="text-charcoal">add to home screen</strong>
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-brass text-ivory flex items-center justify-center text-[10px] font-medium shrink-0">3</span>
          <span>tap <strong className="text-charcoal">add</strong>. the monogram appears on your home screen.</span>
        </li>
      </ol>
      <p className="mt-3 text-[10px] text-taupe italic flex items-center gap-1">
        <ArrowDown className="w-3 h-3" />
        can&apos;t see the safari bar? swipe down from the top or tap the very bottom edge of the screen.
      </p>
    </div>
  );
}
